import { FetchService } from '@foundation-player/fetch-service';
import type { VideoThumbnail } from '@foundation-player/types';
import { parse as vttParser } from '@plussub/srt-vtt-parser';

interface ThumbnailVtt {
  /**
   * range for thumbnail to be shown inclusiv start and excluding end
   */
  range: {
    /**
     * inclusive start of range
     */
    start: number;
    /**
     * exclusive end of range
     */
    end: number;
  };
  /**
   * image to be shown with coordinates
   */
  image: VideoThumbnail;
}

/**
 * parse timestrings from WebVTT
 *
 * @param timeString - in Format HH:MM:SS.sss
 * @returns time in seconds
 *
 * the Format looks like this:
 *  - HH: hours with leading zeros
 *  - MM: minutes with leading zeros
 *  - SS: seconds with leading zeros
 *  - sss: milliseconds with leading zeros
 *
 *
 * @example
 * ```ts
 * parsetime("02:15:06.500") // => 8106.5
 * ```
 *
 * @hidden
 * @internal
 */
export function parseTime(timeString: string): number {
  return [...timeString.matchAll(/(\d{2}):(\d{2}):(\d{2}\.\d{3})/g)][0]
    .slice(1) // remove full match
    .map(parseFloat)
    .reverse()
    .reduce((acc, curr, idx) => acc + curr * 60 ** idx);
}

/**
 * parses the image url from WebVTT and adds base
 *
 * @param base - base url as prefix for image
 * @param rawUrl - url containing hash param for coordinates
 * @returns Thumbnail with coordinates {@link VideoThumbnail}
 *
 * @example
 * ```ts
 * parseImageUrl("https://example.com", "someimage.jpg#xywh=800,450,400,225")
 * // => { url: "https://example.com/someimage.jpg", coords: { x: 800, y: 450, w: 400, h: 225 }}
 * ```
 * @hidden
 * @internal
 */
export function parseImageUrl(base: string, rawUrl: string): VideoThumbnail {
  const coordsKey = 'xywh';
  const [imageUrl, coordsString] = rawUrl.split('#xywh=');
  const url = base + '/' + imageUrl;

  const coords = coordsString
    .split(',')
    .map(parseFloat)
    .reduce(
      (coords, value, idx) => {
        const index = coordsKey[idx] as keyof VideoThumbnail['coords'];
        coords[index] = value;
        return coords;
      },
      {} as VideoThumbnail['coords'],
    );
  return { url, coords };
}

/**
 * @alpha
 */
export class Thumbnails {
  private readonly thumbnails: Promise<ThumbnailVtt[]>;
  private readonly base: string;
  private readonly fetchService = new FetchService();
  private readonly preloadedImages = new Set<string>();

  constructor(
    url: string,
    private readonly preload?: boolean,
  ) {
    const u = new URL(url);
    u.pathname = u.pathname.split('/').slice(0, -1).join('/');
    this.base = u.href;
    this.thumbnails = this.fetchService
      .get<string>(url)
      .then((a) => a.getBody())
      .then((rawVtt: string) => {
        return vttParser(rawVtt).entries.map((entry) => {
          return {
            range: {
              start: entry.from / 1000,
              end: entry.to / 1000,
            },
            image: parseImageUrl(this.base, entry.text),
          };
        });
      })
      .catch(() => {
        console.error(`Failed to load thumbnails from ${url} `);
        return [];
      });
  }

  public getThumbnail(second: number) {
    return this.thumbnails.then(
      (thumbnails) =>
        thumbnails.find((thumbnail) => second >= thumbnail.range.start && second < thumbnail.range.end)?.image,
    );
  }

  private preloadImage(image: string) {
    if (this.preload && !this.preloadedImages.has(image)) {
      this.preloadedImages.add(image);
      const img = new Image();
      img.src = image;
    }
  }
}
